import { Injectable } from '@angular/core';
import { CurrentUserRolesProvider } from '../../organizations/providers/current-user-roles-provider';
import { Gender } from "../../common/models/gender";
import { ContractType } from "../../common/models/contract-type";
import { Coach } from "../../common/models/coach";
import { Team } from "../../common/models/team";
import { AthleteItemRightsList, AthleteRights, mapUserRightsToAthleteItemRightsList } from "./athletes-rights";
import { UtilsProvider } from "../../common/providers/utils-provider.service";
import { ApiBaseService } from "../../common/providers/api-base-service";
import { CurrentUser } from "../../common/providers/current-user";
import { AddressesProvider, AddressRow } from "../../addresses/providers/addresses-provider";
import { Athlete } from "../../common/models/athlete";
import { User } from "../../common/models/user";
import { map, switchMap } from "rxjs/operators";
import { Address } from "../../common/models/address";
import { forkJoin, Observable, of } from "rxjs";
import { Paginator } from "../../common/models/paginator";


@Injectable({
    providedIn: 'root'
})
export class AthletesProvider {
    public Ready: Promise<boolean> = new Promise((resolve, reject) => resolve(false));
    private athletesRights: AthleteRights = new AthleteRights(this._utils);


    constructor(
        private _utils: UtilsProvider,
        private _api: ApiBaseService,
        private _addressesProvider: AddressesProvider,
        private _currentUser: CurrentUser,
        private _currentUserRoles: CurrentUserRolesProvider,
        private api: ApiBaseService,
    ) {

    }
    public get(organizationId:number, userId: number): Observable<User> {
      return this.api.call('get', `organizations/${organizationId}/athletes/${userId}`, {}, this._currentUser.getAccessToken()).pipe(
        map((data: any) => User.fromAPI(data))
      );
    }

    public getIn(organizationId:number, userIds: number[]): Observable<User[]> {
      return this.api.call('get', `organizations/${organizationId}/athletes/`, {
        userId: userIds
      }, this._currentUser.getAccessToken()).pipe(
        map((result: any) =>  User.fromAPIList(result.data)),
      );
    }

    public getAthletes(organizationId: number, teamId?: number): Observable<User[]> {
      let params: any = {
        per_page: 0,
        order: 'name_asc',
      };
      if (teamId) {
        params = {
          ...params,
          teamId: teamId,
        }
      }

      return this.api.call('get', `organizations/${organizationId}/athletes/`, params, this._currentUser.getAccessToken()).pipe(
        map((result: any) => User.fromAPIList(result.data))
      );
    }

    public createAthleteRow(organizationId: number, user: User): AthleteRow {
      return this._athleteRowFromUser(user, organizationId);
    }

    private _athleteRowFromUser(user: User, organizationId: number): AthleteRow {

        return {
          user: User.fromAPI(user),
          athlete: Athlete.fromAPI(user.athlete ?? {}),
          gender: user.athlete?.gender ?? (user.athlete?.gender ? Gender.fromAPI(user.athlete?.gender!) : {} as Gender),
          contractType: user.athlete?.contractType ?? (user.athlete?.contractTypeId ? ContractType.fromAPI({id: user.athlete!.contractTypeId}) : {} as ContractType),
          addresses: user.athlete?.addresses?.map((address: Address) => (Address.fromAPI(address))) ?? [],
          coaches: User.fromAPIList(
            user.athlete?.coaches
              ?.map((coach: Coach) => coach.user)
              ?.filter((user): user is User => user !== undefined) ?? []
          ),
          coachNames: user.athlete?.coaches?.map((coach: Coach) => `${coach.user?.firstName} ${coach.user?.lastName}`).join(', ') ?? '',
          teams: user.athlete?.teams ? Team.fromAPIList(user.athlete!.teams!) : [],
          teamNames: user.athlete?.teams?.map((team: Team) => team.name).join(', ') ?? '',
          rights: mapUserRightsToAthleteItemRightsList(user.rights ?? []),
        };
    }

    public getAthleteRow(organizationId: number, userId: number): Observable<AthleteRow> {
      return this.api.call('get',`organizations/${organizationId}/athletes/${userId}`, {}, this._currentUser.getAccessToken())
        .pipe(
          map((user: User) => {
            return this._athleteRowFromUser(user, organizationId)
          }),
        );
    }

    public getAthleteRows(organizationId: number): Observable<AthleteRow[]> {
      return this.api.call('get',`organizations/${organizationId}/athletes/`, {
        per_page: 0,
        sort: 'name_asc',
      }, this._currentUser.getAccessToken())
      .pipe(
        map((result: Paginator) => User.fromAPIList(result.data)),
        map((users: User[]) => users.map((user: User) => this._athleteRowFromUser(user, organizationId))),
      );
    }

    public getAthletesPaginator({organizationId, pageNumber, orderBy, userName, coachName}: {organizationId: number, pageNumber?: number, orderBy?: string, userName?: string, coachName?: string}): Observable<Paginator> {
      return this.api.call('get',`organizations/${organizationId}/athletes/`, {
        page: pageNumber,
        sort: orderBy,
        userName: userName,
        coachName: coachName,
      }, this._currentUser.getAccessToken())
        .pipe(
          map((result: Paginator) => result),
        );
    }

    public patchAthlete(organizationId: number, userId: number, data:Object): Observable<any> {
      return this.api.call('patch',`organizations/${organizationId}/athletes/${userId}`, data, this._currentUser.getAccessToken()
      );
    }

    public deleteAthlete(organizationId: number, userId: number): Observable<void> {
      return this.api.call('delete',`organizations/${organizationId}/athletes/${userId}`, {}, this._currentUser.getAccessToken());
    }

    public getGenders(): Observable<Gender[]> {
      return this._api.call('get','nosync/common-data/genders/', {}, this._currentUser.getAccessToken())
      .pipe(
        map((data: Gender[]) => Gender.fromAPIList(data))
      );
    }

    public getContractTypes(): Observable<ContractType[]> {
      return this._api.call('get','nosync/common-data/contract-types', {}, this._currentUser.getAccessToken())
      .pipe(
        map((data: ContractType[]) => ContractType.fromAPIList(data))
      );
    }

  /*** END NEW REQUESTS ***/
    /*** ****************************** ***/
    /*** ******************************************** ***/
    /*** ****************************** ***/
    /*** OLD REQUESTS ***/


    public getWithUserId(userId:number): Observable<Athlete> {
      return this._api.call('get',`athletes/user/${userId}`).pipe(
        map((data: any) => Athlete.fromAPI(data))
      );
    }

    // public getAthleteRows(organizationId: number): Observable<AthleteRow[]> {
    //   throw ("Use new method getAthletesRows");
    // }
    //   return forkJoin([
    //     this.getAthletes(organizationId),
    //     this._usersProvider.getByOrganizationId(organizationId),
    //     this._addressesProvider.getAddressRows(organizationId),
    //     this._coachesProvider.getAthleteCoachRelations(),
    //     this._teamsProvider.getAthleteTeamRelations(),
    //     this._coachesProvider.getCoaches(organizationId),
    //     this._teamsProvider.getTeams(organizationId),
    //     this._organizationsProvider.getMembershipsByOrganizationId(organizationId),
    //     of(this._currentUserRoles.getRolesIds(organizationId)),
    //     this._guestsProvider.getGuests(organizationId),
    //   ]).pipe(
    //     map(([athletes, users, addresses, athleteCoaches, athleteTeams, coaches, teams,  memberships, rolesIds, guests]) => {
    //       this.availableAthleteCoachRelations = athleteCoaches;
    //       this.availableAthleteTeamRelations = athleteTeams;
    //       this.availableCoaches = coaches;
    //       this.availableTeams = teams;
    //       this.availableAthleteGuestRelations = guests.map((guest: any) => {
    //         return new AthleteGuest({
    //           athleteId: guest.athleteId,
    //           guestId: guest.guestId,
    //           teamId: guest.teamId,
    //         });
    //       });
    //       this.availableGuests = guests;
    //
    //       const rows: AthleteRow[] = [];
    //
    //       athletes.forEach((athlete: Athlete) => {
    //
    //         this.athletesRights.evaluateItemRight({
    //             user: this._currentUser.user!,
    //             organizationId,
    //             athlete,
    //             rolesIds: rolesIds,
    //             athleteCoachRelations: this.availableAthleteCoachRelations,
    //             athleteGuestRelations: this.availableAthleteGuestRelations,
    //             athleteMemberships: memberships,
    //             coaches: this.availableCoaches,
    //             guests: this.availableGuests,
    //         });
    //
    //         // ajoute l'athlète à la liste
    //         rows.push(this._toAthleteRow(athlete, users, addresses, this.athletesRights.getItemRightsList()));
    //       });
    //       return rows;
    //     }),
    //   );
    // }

    // public getAthleteRow(organizationId: number, athleteId: number): Observable<AthleteRow> {
    //   return forkJoin([
    //     this.get(organizationId, athleteId),
    //     this._usersProvider.getByOrganizationId(organizationId),
    //     this._addressesProvider.getAddressRows(organizationId),
    //     this._coachesProvider.getAthleteCoachRelations(),
    //     this._teamsProvider.getAthleteTeamRelations(),
    //     this._coachesProvider.getCoaches(organizationId),
    //     this._teamsProvider.getTeams(organizationId),
    //     this._organizationsProvider.getMembershipsByOrganizationId(organizationId),
    //     this._currentUserRoles.getRolesIds(organizationId),
    //     this._guestsProvider.getGuests(organizationId),
    //
    //   ]).pipe(
    //     map(([athlete, users, addresses, athleteCoaches, athleteTeams, coaches, teams, memberships, rolesIds, guests]) => {
    //         this.availableAthleteCoachRelations = athleteCoaches
    //         this.availableAthleteTeamRelations = athleteTeams
    //         this.availableCoaches = coaches
    //         this.availableTeams = teams
    //         this.availableAthleteGuestRelations = guests.map((guest: any) => {
    //           return new AthleteGuest({
    //             athleteId: guest.athleteId,
    //             guestId: guest.guestId,
    //             teamId: guest.teamId,
    //           });
    //         });
    //         this.availableGuests = guests
    //
    //         // charge les droits que j'ai sur l'athlète
    //         this.athletesRights.evaluateItemRight({
    //             user: this._currentUser.user!,
    //             organizationId,
    //             athlete,
    //             rolesIds: this._currentUserRoles.getRolesIds(organizationId),
    //             athleteCoachRelations: this.availableAthleteCoachRelations,
    //             athleteGuestRelations: this.availableAthleteGuestRelations,
    //             athleteMemberships: memberships,
    //             coaches: this.availableCoaches,
    //             guests: this.availableGuests,
    //         });
    //
    //         // return this._toAthleteRow(athlete, users, addresses, this.athletesRights.getItemRightsList());
    //       }),
    //   );
    // }

    public saveAthlete(organizationId: number, athleteRow: AthleteRow): Observable<AthleteRow> {
      return this._api.call('post','organizations/' + organizationId + '/athletes/' + (athleteRow.user.id ? athleteRow.user.id : 0),
          athleteRow,
          this._currentUser.getAccessToken(),
        false
      ).pipe(
        map((dataSaved: any) => {
          return this.createAthleteRow(organizationId, User.fromAPI(dataSaved));
        })
      );
    }

    public saveAthleteCoaches(organizationId: number, athleteRow: AthleteRow): Observable<AthleteRow> {
      return this._api.call('post','organizations/' + organizationId + '/athletes/' + (athleteRow.user.id ?? 0) + '/coaches',
          athleteRow,
          this._currentUser.getAccessToken()
      ).pipe(
        switchMap((data: any) => this.getAthleteRow(organizationId, data.athlete.userId))
      );
    }

    public saveAthleteTeams(organizationId: number, removeCoachRelations: boolean, removeGuestRelations: boolean, athleteRow: AthleteRow): Observable<AthleteRow> {
      return this.api.call('post', 'organizations/' + organizationId + '/athletes/' + (athleteRow.user.id ?? 0) + '/teams',
        {
            atr: athleteRow,
            rer: removeCoachRelations,
            reg: removeGuestRelations
        },
        this._currentUser.getAccessToken()
      ).pipe(
        switchMap((data: any) => this.getAthleteRow(organizationId, data.athlete.userId))
      );
    }

    private _getCoachConcatenatedNames(coacheslist: any[], users: User[]): string {

        const names: string[] = [];
        let user: User = {} as User;
        for (let i = 0; i < coacheslist.length; i++) {
            user = this._utils.findIn(coacheslist[i], users, 'id');
            if (user) {
                names.push(user.firstName + ' ' + user.lastName);
            }
        }

        return names.join(', ');
    }

    private _getTeamConcatenatedNames(teamslist: any[], teams: Team[]): string {

        const names: string[] = [];
        let team: Team = {} as Team;
        for (let i = 0; i < teamslist.length; i++) {
            team = this._utils.findIn(teamslist[i], teams, 'id');
            if (team) {
                names.push(team.name);
            }
        }

        return names.join(', ');
    }

    // private _toAthleteRow(athlete: Athlete, users: User[], addresses: AddressRow[], athleteRightsList: AthleteItemRightsList): AthleteRow {
    //     let teamIds = this._utils.findManyIn([athlete.id], this.availableAthleteTeamRelations, 'athleteId');
    //     teamIds = this._utils.getKeyValues(teamIds, 'teamId');
    //
    //     let coachIds = this._utils.findManyIn([athlete.id], this.availableAthleteCoachRelations, 'athleteId');
    //     coachIds = this._utils.getKeyValues(coachIds, 'coachId');
    //
    //     // userIds correspondant aux coachIds
    //     const coachUsers = this._utils.findManyIn(coachIds, this.availableCoaches, 'id');
    //     const coachUserIds = this._utils.getKeyValues(coachUsers, 'userId');
    //
    //     const row: AthleteRow = {
    //         user: this._utils.findIn(athlete.userId, users, 'id'),
    //         athlete,
    //         gender: this._utils.findIn(athlete.genderId, this.availableGenders, 'id'),
    //         contractType: this._utils.findIn(athlete.contractTypeId, this.availableContractTypes, 'id'),
    //         addresses: this._utils.findManyInSubObject([athlete.id], addresses, 'address', 'athleteId'),
    //         coaches: this._utils.findManyIn(coachIds, this.availableCoaches, 'id'),
    //         coachNames: this._getCoachConcatenatedNames(coachUserIds, users),
    //         teams: this._utils.findManyIn(teamIds, this.availableTeams, 'id'),
    //         teamNames: this._getTeamConcatenatedNames(teamIds, this.availableTeams),
    //         rights: athleteRightsList,
    //     };
    //
    //     return row;
    // }

}

export interface AthleteRow {
    user: User;
    athlete: Athlete;
    gender: Gender;
    contractType: ContractType;
    addresses: Address[];
    coaches: Coach[];
    coachNames: string;
    teams: Team[];
    teamNames: string;
    rights?: AthleteItemRightsList;
}
